import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Ask a named colleague to approve something on this record. It lands in their My Work. */
export default function RequestApprovalDialog({ open, onOpenChange, record }) {
  const [people, setPeople] = useState([]);
  const [approver, setApprover] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(record?.suggested_title || "");
    setNote(""); setDue(""); setApprover("");
    axios.get(`${API}/approvals/people`).then((r) => setPeople(r.data.people || [])).catch(() => {});
  }, [open, record?.suggested_title]);

  const person = people.find((p) => p.id === approver);

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/approvals`, {
        title: title.trim(),
        note: note.trim() || null,
        approver_id: person?.id,
        approver_name: person?.name,
        due_date: due || null,
        link_type: record?.link_type,
        link_id: record?.link_id,
        link_name: record?.link_name,
        link_path: record?.link_path,
        amount_label: record?.amount_label,
      });
      toast.success(`Approval requested from ${person?.name}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that request");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white border-[#DAD5CA]" data-testid="request-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <ShieldCheck className="w-4 h-4 text-[#A8320A]" /> Request an approval
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[13px] text-[#59554D]">
            {record?.link_name ? <>This is about <span className="font-semibold">{record.link_name}</span>. </> : null}
            The person you choose sees it in their My Work until they approve it.
          </p>
          <div>
            <label className="text-[11.5px] font-semibold text-[#59554D]">Who approves this?</label>
            <Select value={approver} onValueChange={setApprover}>
              <SelectTrigger className="mt-1 h-9 border-[#DAD5CA]" data-testid="approval-approver">
                <SelectValue placeholder="Pick a person" />
              </SelectTrigger>
              <SelectContent>
                {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-[#59554D]">What needs approving?</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="approval-title"
                   className="mt-1 h-9 border-[#DAD5CA]" placeholder="e.g. 15% discount on the annual quote" />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-[#59554D]">Anything they should know? (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="approval-note"
                      className="mt-1 border-[#DAD5CA]" placeholder="Context that helps them decide" />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-[#59554D]">Needed by (optional)</label>
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="approval-due"
                   className="mt-1 h-9 border-[#DAD5CA] w-44" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#DAD5CA]">Cancel</Button>
          <Button onClick={send} disabled={!approver || !title.trim() || busy} data-testid="approval-send"
                  className="bg-[#FFD108] hover:bg-[#E0B800] text-[#121212] font-semibold disabled:bg-[#E6E2D9] disabled:text-[#B5AFA3]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send the request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
